import React from 'react'
import { InputField } from '@components/Form/InputField'
import { Button } from '@components/Basic/ButtonNbe'
import { Trans, useTranslation } from 'react-i18next'
import {
  HeaderSearchWrapper,
  TopHeader,
  SubHeader,
  InputWrapper,
  SideLogoStyled,
  ClearInputButton,
  TopHeaderMiddle,
} from './styled'
import { AiOutlineCloseCircle, AiOutlineSearch } from 'react-icons/ai'
import { MdOutlineRefresh } from 'react-icons/md'
import ReactTooltip from 'react-tooltip'
import { FieldsProgressBar } from '@features/nbee/FieldsMappingForm/HeaderSearch/FieldsProgressBar'
import { WelcomeMessageProps } from '@features/nbee/FieldsMappingForm'

export interface HeaderSearchProps {
  totalFields: number
  mappedFields: number
  sourceName: string
  sourceLogoUri: string
  destinationName: string
  destinationLogoUri: string
  showAllFields: boolean
  onShowAllFieldsRequest: (showAllFields: boolean) => void
  inputValue: string
  onInputChange: (value: string) => void
  isInTestScreen?: boolean
  showFilterButton: boolean
  onRefetchFieldRequest: () => void
  isReloadingAllBridgeFields: boolean
  isReadonlyModeEnabled?: boolean
  maxFields?: number
  welcomeMessageProps?: WelcomeMessageProps
}

export type SideLogoProps = {
  logoPosition: 'left' | 'right'
  logoUri?: string
  name?: string
}

const SideLogo: React.FC<SideLogoProps> = ({ logoPosition, logoUri, name }) => {
  return (
    <SideLogoStyled logoPosition={logoPosition}>
      <img src={logoUri} alt={name} title={name} />
      {name && <p>{name}</p>}
    </SideLogoStyled>
  )
}

export const HeaderSearchComponent: React.FC<HeaderSearchProps> = ({
  totalFields,
  mappedFields,
  sourceName,
  sourceLogoUri,
  destinationName,
  destinationLogoUri,
  inputValue,
  onInputChange,
  showAllFields,
  onShowAllFieldsRequest,
  isInTestScreen,
  showFilterButton,
  onRefetchFieldRequest,
  isReloadingAllBridgeFields,
  isReadonlyModeEnabled,
  maxFields,
  welcomeMessageProps,
}) => {
  const { t } = useTranslation()

  return (
    <HeaderSearchWrapper>
      <TopHeader>
        <SideLogo
          logoPosition={'left'}
          name={sourceName}
          logoUri={sourceLogoUri}
        />
        {welcomeMessageProps?.isWelcomeMessageForm ? (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {welcomeMessageProps.isSmsForm ? (
              <span>
                {t('nbee.fieldsMapping.welcomeMessageSmsDescription')}
              </span>
            ) : (
              <span>
                {t('nbee.fieldsMapping.welcomeMessageEmailDescription')}
              </span>
            )}
          </div>
        ) : null}

        {isInTestScreen || !totalFields ? null : (
          <div style={{ textAlign: 'center' }}>
            <TopHeaderMiddle
              isReloadingAllBridgeFields={isReloadingAllBridgeFields}
            >
              {!isReadonlyModeEnabled ? (
                <>
                  <ReactTooltip
                    id='topbar-refetch'
                    place='top'
                    type='dark'
                    textColor='white'
                    effect='solid'
                  >
                    <span>
                      {t('nbee.fieldsMapping.refetchBridgeFieldButton')}
                    </span>
                  </ReactTooltip>
                  <span
                    data-tip
                    data-for='topbar-refetch'
                    onClick={() => {
                      onRefetchFieldRequest()
                    }}
                  >
                    <MdOutlineRefresh size={24} />
                  </span>
                </>
              ) : null}
              {maxFields ? (
                <>
                  <ReactTooltip
                    id='max-fields'
                    place='top'
                    type='dark'
                    textColor='white'
                    effect='solid'
                  >
                    <span>
                      <Trans
                        ns={'all'}
                        i18nKey={'nbee.fieldsMapping.fieldCounterLimitation'}
                        components={{
                          br: <br />,
                        }}
                        values={{ maxFields: maxFields }}
                      />
                    </span>
                  </ReactTooltip>
                  <p
                    data-tip
                    data-for='max-fields'
                    style={{
                      color: mappedFields > maxFields ? '#cc2029' : '#121232',
                      cursor: 'help',
                    }}
                  >
                    {t('nbee.fieldsMapping.totalMaxField', {
                      mappedFields,
                      maxFields,
                    })}
                  </p>
                </>
              ) : (
                t('nbee.fieldsMapping.totalMapped', {
                  mappedFields,
                  totalFields,
                })
              )}
            </TopHeaderMiddle>
            {maxFields && (
              <div>
                <FieldsProgressBar
                  maxFields={maxFields}
                  mappedFields={mappedFields}
                />
              </div>
            )}
          </div>
        )}
        <SideLogo
          logoPosition={'right'}
          name={destinationName}
          logoUri={destinationLogoUri}
        />
      </TopHeader>
      {!isInTestScreen && totalFields > 0 ? (
        <SubHeader>
          <InputWrapper>
            <InputField
              $fluid
              name={'search-input'}
              type={'text'}
              placeholder={t('nbee.fieldsMapping.searchFields')}
              value={inputValue}
              onChange={({ target: { value } }) => {
                onInputChange(value)
              }}
              slotLeft={
                <AiOutlineSearch size={'1em'} style={{ marginLeft: '.8rem' }} />
              }
              slotRight={
                inputValue ? (
                  <ClearInputButton
                    onClick={() => {
                      onInputChange('')
                    }}
                  >
                    <AiOutlineCloseCircle />
                  </ClearInputButton>
                ) : null
              }
            />
          </InputWrapper>
          {showFilterButton ? (
            <Button
              type={'button'}
              onClick={() => {
                onShowAllFieldsRequest(!showAllFields)
              }}
              $variant={'filter'}
            >
              {showAllFields
                ? t('nbee.fieldsMapping.hideUnmapped')
                : t('nbee.fieldsMapping.showUnmapped')}
            </Button>
          ) : null}
        </SubHeader>
      ) : null}
    </HeaderSearchWrapper>
  )
}
